import type { AttachedFile } from "@/lib/api";
import { useStore } from "@/lib/store";
import { inTauri } from "@/lib/tools";
import { formatBytes } from "@/lib/prompt-builder";
import { PASTE_COLLAPSE_CHARS, nextPasteId, pasteToken, referencedPasteIds, tokenEndingAt } from "@/lib/pastes";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

const EMPTY_FILES: AttachedFile[] = [];

const MAX_FILE_BYTES = 100 * 1024 * 1024;
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

type ChatInputProps = {
  onSend: (message: string, files?: AttachedFile[]) => void;
  isStreaming: boolean;
  autoFocus?: boolean;
  draftKey: string;
  topSlot?: React.ReactNode;
};

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function transcodeToPng(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function processFiles(fileList: FileList | File[]): Promise<AttachedFile[]> {
  const results: AttachedFile[] = [];
  const files = Array.from(fileList);
  for (const file of files) {
    if (IMAGE_TYPES.has(file.type)) {
      if (file.size > MAX_IMAGE_BYTES) continue;
      let dataUrl: string;
      if (file.type === "image/webp") {
        try {
          dataUrl = await transcodeToPng(file);
        } catch (e) {
          console.warn("WebP transcode failed, sending raw:", e);
          const buffer = await file.arrayBuffer();
          dataUrl = `data:${file.type};base64,${arrayBufferToBase64(buffer)}`;
        }
      } else {
        const buffer = await file.arrayBuffer();
        dataUrl = `data:${file.type};base64,${arrayBufferToBase64(buffer)}`;
      }
      results.push({ type: "image", name: file.name, content: "", dataUrl, source: file });
      continue;
    }
    if (file.size > MAX_FILE_BYTES) continue;
    results.push({ type: "file", id: "", name: file.name, mime: file.type, size: file.size, lines: null, preview: null, source: file });
  }
  return results;
}

export function ChatInput({ onSend, isStreaming, autoFocus, draftKey, topSlot }: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelOpen = useStore((s) => s.artifactPanelOpen);
  const text = useStore((s) => s.drafts[draftKey]?.text ?? "");
  const filesFromStore = useStore((s) => s.drafts[draftKey]?.files);
  const files = filesFromStore ?? EMPTY_FILES;
  const setDraftText = useStore((s) => s.setDraftText);
  const setDraftFiles = useStore((s) => s.setDraftFiles);
  const clearDraft = useStore((s) => s.clearDraft);
  const workspacePath = useStore((s) => s.activeWorkspacePath);
  const setActiveWorkspace = useStore((s) => s.setActiveWorkspace);
  const [dragging, setDragging] = useState(false);

  const handlePickFolder = async () => {
    if (!inTauri) return;
    try {
      const mod = await import("@tauri-apps/plugin-dialog");
      const picked = await mod.open({ directory: true, multiple: false, title: "Choose workspace folder" });
      if (typeof picked === "string" && picked) await setActiveWorkspace(picked);
    } catch (e) {
      console.warn("Folder pick failed:", e);
    }
  };

  const detachWorkspace = async () => {
    await setActiveWorkspace(null);
  };

  const openWorkspaceInFinder = async () => {
    if (!inTauri || !workspacePath) return;
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("open_in_finder", { path: workspacePath });
    } catch (e) {
      console.warn("Failed to open in Finder:", e);
    }
  };

  const attachments = files.filter((f) => f.type !== "paste");

  const workspaceName = workspacePath ? workspacePath.split("/").filter(Boolean).pop() ?? workspacePath : "";

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
    el.style.overflowY = el.scrollHeight > 200 ? "auto" : "hidden";
  };

  useEffect(() => {
    resizeTextarea();
  }, [text]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (autoFocus) {
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      requestAnimationFrame(() => {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    }
  }, [autoFocus, draftKey]);

  useEffect(() => {
    const hasFiles = (e: globalThis.DragEvent) => {
      const types = Array.from(e.dataTransfer?.types ?? []);
      if (types.includes("Files")) return true;
      const items = e.dataTransfer?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].kind === "file") return true;
        }
      }
      return false;
    };

    let resetTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReset = () => {
      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = setTimeout(() => setDragging(false), 80);
    };
    const cancelReset = () => {
      if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
    };

    const onDragEnter = (e: globalThis.DragEvent) => {
      e.preventDefault();
    };
    const onDragOver = (e: globalThis.DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      if (!hasFiles(e)) return;
      cancelReset();
      setDragging(true);
      scheduleReset();
    };
    const onDragLeave = (e: globalThis.DragEvent) => {
      scheduleReset();
    };
    const onDrop = async (e: globalThis.DragEvent) => {
      cancelReset();
      setDragging(false);
      if (!hasFiles(e)) return;
      e.preventDefault();
      const fromFiles = e.dataTransfer ? Array.from(e.dataTransfer.files) : [];
      const fromItems: File[] = [];
      if (e.dataTransfer?.items) {
        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          const it = e.dataTransfer.items[i];
          if (it.kind === "file") {
            const f = it.getAsFile();
            if (f) fromItems.push(f);
          }
        }
      }
      const snapshot = fromItems.length > fromFiles.length ? fromItems : fromFiles;
      if (snapshot.length === 0) return;
      const newFiles = await processFiles(snapshot);
      if (newFiles.length > 0) {
        const current = useStore.getState().drafts[draftKey]?.files ?? [];
        setDraftFiles(draftKey, [...current, ...newFiles]);
      }
      textareaRef.current?.focus();
    };

    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
      cancelReset();
    };
  }, [draftKey, setDraftFiles]);

  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.target === textareaRef.current) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length !== 1) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      textareaRef.current?.focus();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSend = useCallback(() => {
    const value = text.trim();
    const referenced = referencedPasteIds(value);
    const outgoing = files.filter((f) => f.type !== "paste" || referenced.has(f.id));
    if (!value && outgoing.length === 0) return;
    onSend(value, outgoing.length > 0 ? outgoing : undefined);
    clearDraft(draftKey);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }, [onSend, text, files, clearDraft, draftKey]);

  const isCoarsePointer = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

  const placeCaret = (position: number) => {
    requestAnimationFrame(() => textareaRef.current?.setSelectionRange(position, position));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Backspace") {
      const { selectionStart, selectionEnd } = e.currentTarget;
      const token = selectionStart === selectionEnd ? tokenEndingAt(text, selectionStart) : null;
      if (token) {
        e.preventDefault();
        setDraftText(draftKey, text.slice(0, token.start) + text.slice(token.end));
        placeCaret(token.start);
        return;
      }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (!isStreaming) handleSend();
      return;
    }
    if (isCoarsePointer) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isStreaming) handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraftText(draftKey, e.target.value);
  };

  const removeFile = (name: string) => {
    setDraftFiles(draftKey, files.filter((f) => f.name !== name));
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length > 0) return;
    const pasted = e.clipboardData.getData("text/plain");
    if (!pasted) return;
    if (pasted.length <= PASTE_COLLAPSE_CHARS) return;
    e.preventDefault();
    const id = nextPasteId(files);
    const token = pasteToken(id, pasted);
    const { selectionStart, selectionEnd } = e.currentTarget;
    setDraftText(draftKey, text.slice(0, selectionStart) + token + text.slice(selectionEnd));
    setDraftFiles(draftKey, [...files, { type: "paste", id, name: `Pasted text #${id}`, content: pasted }]);
    placeCaret(selectionStart + token.length);
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const newFiles = await processFiles(e.target.files);
    if (newFiles.length > 0) setDraftFiles(draftKey, [...files, ...newFiles]);
    e.target.value = "";
    textareaRef.current?.focus();
  };

  return (
    <>
      {dragging && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-primary/10 backdrop-blur-sm pointer-events-none">
          <div className="rounded-xl border-2 border-dashed border-primary bg-background/80 px-8 py-6 text-center shadow-lg">
            <i className="fa-solid fa-cloud-arrow-up text-3xl text-primary" />
            <p className="mt-2 text-sm font-medium">Drop files to attach</p>
            <p className="text-xs text-muted-foreground">Images and text files supported</p>
          </div>
        </div>
      )}
    <div
      className={`border-t bg-background p-3 sm:p-4 fixed bottom-0 ${panelOpen ? "right-0 lg:right-[32rem]" : "right-0"}`}
      style={{ left: "var(--sb-w, 0px)", paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto max-w-4xl">
        {topSlot}
        {workspacePath && (
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-orange-500/10 px-2 py-0.5 text-orange-700 dark:text-orange-300">
              <button
                onClick={openWorkspaceInFinder}
                className="inline-flex items-center gap-1.5 hover:underline"
                title={`Reveal ${workspacePath} in Finder`}
              >
                <i className="fa-solid fa-folder-open" />
                <span className="font-mono">{workspaceName}</span>
              </button>
              <button onClick={detachWorkspace} className="hover:text-destructive" title="Detach workspace">
                <i className="fa-solid fa-xmark" />
              </button>
            </span>
            <span className="text-muted-foreground">workspace tools active</span>
          </div>
        )}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {attachments.map((f) => (
              <span key={f.name} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {f.type === "image" ? (
                  <img src={f.dataUrl} alt={f.name} className="size-4 rounded object-cover" />
                ) : (
                  <i className="fa-solid fa-file-lines text-[10px]" />
                )}
                {f.name}
                {f.type === "file" && <span className="opacity-70">{formatBytes(f.size)}</span>}
                <button onClick={() => removeFile(f.name)} className="hover:text-destructive ml-0.5">
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="relative flex">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInput}
          />
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            enterKeyHint={isCoarsePointer ? "enter" : "send"}
            placeholder="Type a message..."
            rows={1}
            className={`w-full resize-none overflow-hidden rounded-[64px] sm:rounded-lg border bg-muted/50 py-2.5 pl-5 sm:pl-3 ${inTauri ? "pr-28" : "pr-20"} text-base outline-none placeholder:text-muted-foreground focus:border-primary`}
          />
          <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1">
            {inTauri && (
              <button
                onClick={handlePickFolder}
                className="flex size-8 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted/70 hover:text-orange-500"
                title={workspacePath ? `Change workspace (current: ${workspacePath})` : "Attach a workspace folder"}
              >
                <i className="fa-solid fa-folder-tree" />
              </button>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex size-8 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-muted/70 hover:text-primary"
              title="Attach files"
            >
              <i className="fa-solid fa-paperclip" />
            </button>
            <button
              onClick={handleSend}
              disabled={isStreaming}
              className="flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-30"
            >
              {isStreaming ? (
                <i className="fa-solid fa-spinner-third fa-spin" />
              ) : (
                <i className="fa-solid fa-paper-plane-top" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
